const FRAMEWORK_ID_PATTERNS = [
    /^__/,
    /^radix-/,
    /^headlessui-/,
    /^aria-/,
    /^[0-9]/,
    /^step-[0-9]+$/,
    /^results?-heading$/i,
    /^the-(problem|bottom-line)$/i,
];
const EXCLUDE_ANCESTOR = 'header, nav, footer, dialog, [role="dialog"], [aria-modal="true"]';
const MAX_LABEL_LEN = 80;
function titleCase(slug) {
    return slug.split(/[-_]/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}
function truncate(s) {
    return s.length > MAX_LABEL_LEN ? s.slice(0, MAX_LABEL_LEN - 1) + '…' : s;
}
function isNavigable(el, denyIdPatterns) {
    if (!el.id || el.id.length > 80)
        return false;
    if (denyIdPatterns.some((re) => re.test(el.id)))
        return false;
    if (el.closest(EXCLUDE_ANCESTOR))
        return false;
    // Headings are small (~30px tall) but legitimate scroll targets — skip
    // the size filter for them. Containers still get the size filter so
    // tiny icon-only divs don't make it through.
    const isHeading = el.matches('h1, h2, h3');
    const rect = el.getBoundingClientRect?.();
    if (rect) {
        // Zero-area means the element isn't rendered (e.g. display: none).
        if (rect.width === 0 && rect.height === 0)
            return false;
        if (!isHeading && (rect.width < 100 || rect.height < 50))
            return false;
    }
    return true;
}
function cssEscapeIdent(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        return CSS.escape(value);
    return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
function toEntry(el) {
    const customLabel = el.getAttribute('data-voice-label');
    const heading = el.matches('h1, h2, h3') ? el : el.querySelector('h1, h2, h3');
    const ariaLabel = el.getAttribute('aria-label');
    const attr = el.getAttribute('data-voice-section') ?? el.id;
    const raw = customLabel?.trim() || heading?.textContent?.trim() || ariaLabel || titleCase(attr);
    const selector = el.id
        ? `#${cssEscapeIdent(el.id)}`
        : `[data-voice-section="${attr.replace(/[\\"]/g, '\\$&')}"]`;
    return { label: truncate(raw), selector };
}
const GENERATED_ID_PREFIX = 'moss-section-';
const MAX_SLUG_LEN = 60;
function slugify(text) {
    return text
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_SLUG_LEN);
}
/**
 * For vanilla customer sites that don't annotate their markup, walk the
 * document's h2/h3 elements and assign generated IDs to any heading that
 * doesn't already have one. Skips headings inside header/nav/footer and
 * keeps existing IDs intact. Lets the voice agent scroll to any prominent
 * heading without the customer touching their source.
 */
function ensureHeadingIds(doc) {
    const headings = doc.querySelectorAll('h2, h3');
    if (!headings.length)
        return;
    const taken = new Set();
    doc.querySelectorAll('[id]').forEach((el) => {
        if (el.id)
            taken.add(el.id);
    });
    headings.forEach((h) => {
        if (h.closest(EXCLUDE_ANCESTOR))
            return;
        // If any ancestor already has an id, the heading is already
        // discoverable via that ancestor — don't create a duplicate target.
        if (h.parentElement?.closest('[id]'))
            return;
        const text = h.textContent?.trim();
        if (!text)
            return;
        const slug = slugify(text);
        if (!slug)
            return;
        // Prefer the closest section/article ancestor — it gives the agent a
        // taller scroll target and reads more naturally to screen readers.
        // Fall back to the heading itself when no such wrapper exists.
        const sectionAncestor = h.closest('section, article');
        const target = sectionAncestor && !sectionAncestor.id ? sectionAncestor : h;
        if (target.id)
            return;
        let candidate = `${GENERATED_ID_PREFIX}${slug}`;
        let n = 2;
        while (taken.has(candidate)) {
            candidate = `${GENERATED_ID_PREFIX}${slug}-${n++}`;
        }
        target.id = candidate;
        taken.add(candidate);
    });
}
export function scanSections(doc, path, options = {}) {
    const denyIdPatterns = options.denyIdPatterns
        ? [...FRAMEWORK_ID_PATTERNS, ...options.denyIdPatterns]
        : FRAMEWORK_ID_PATTERNS;
    const curated = Array.from(doc.querySelectorAll('[data-voice-section]'));
    let elements;
    if (curated.length > 0) {
        elements = curated;
    }
    else {
        if (options.generateHeadingIds ?? true)
            ensureHeadingIds(doc);
        elements = Array.from(doc.querySelectorAll('[id]')).filter((el) => isNavigable(el, denyIdPatterns));
    }
    return {
        type: 'page_context',
        path,
        title: doc.title || '',
        sections: elements.map(toEntry),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
}
